import { useEffect, useMemo, useState } from 'react';

import { PRE_POPULATE_ACTIONS, buildDefaultPartTypeOptions } from './partActionSearchSources';
import { PartConditionOptions } from '../../../../../@types/part.type';
import { Action, PartActionType } from '../../../../../@types/partAction.type';
import {
  PartTypeActionLiveSearchItem,
  PartTypeActionLiveSearchItemCategoryCode,
  PartOrPartTypeSearchData,
} from '../../../../../@types/partType.type';
import { useGetInteractionsPartRequestFormPartTypes } from '../../../../../queries/interactions-query';
import { useSearchPartsOrPartTypes } from '../../../../../queries/live-search-query';
import { useGetPartTypesByClientContract } from '../../../../../queries/part-type-query';

export interface UsePartActionDefaultsArgs {
  clientId: number;
  contractId: number;
  locationId: number;
  serviceId: number;
  actionType: string;
  action: Action;
  reportedSerialNumberPartId?: number;
}

/**
 * Assembles the default (pre-typing) option list for the Part Action search
 * field. Three sources, in priority order:
 *
 *   1. Reported-serial parts/types via `/partOrPartType + reportedSerialNumberPartId`
 *      — surfaces the reported serial as the *first* row when present.
 *   2. Installed part types on this job via `/partTypes/service/{id}/condition/installed`.
 *   3. Client/contract part-type universe via `/partTypes/client/{id}?contractId={id}`
 *      (cms1 parity — same set returned by `part_type_allocations`).
 *
 * DOA's serialised OUT field skips the universe and uses the reported-serial
 * call directly (`serialiseType=serialised`).
 */
export const usePartActionDefaults = ({
  clientId,
  contractId,
  locationId,
  serviceId,
  actionType,
  action,
  reportedSerialNumberPartId,
}: UsePartActionDefaultsArgs): {
  defaults: PartTypeActionLiveSearchItem[];
} => {
  const reportedSerialMutation = useSearchPartsOrPartTypes();
  const outSerialMutation = useSearchPartsOrPartTypes();
  const [reportedSerialResults, setReportedSerialResults] = useState<
    PartTypeActionLiveSearchItem[]
  >([]);
  const [outSerialResults, setOutSerialResults] = useState<PartTypeActionLiveSearchItem[]>([]);

  const isInAction = actionType === PartActionType.INSTALL || action === Action.IN;
  const isOutSerialised =
    action === Action.OUT &&
    (actionType === PartActionType.REPLACE ||
      actionType === PartActionType.REMOVE ||
      actionType === PartActionType.DOA);
  const isPrePopulateAction = PRE_POPULATE_ACTIONS.has(actionType as PartActionType);
  const shouldPrePopulateUniverse = isInAction && isPrePopulateAction;

  // Source #1 — reported-serial fetch for the IN-side pre-populate set.
  useEffect(() => {
    if (!shouldPrePopulateUniverse || !reportedSerialNumberPartId) {
      setReportedSerialResults([]);
      return;
    }
    let cancelled = false;
    const data: PartOrPartTypeSearchData = {
      name: '',
      serialiseType: '',
      clientContractIds: [clientId, contractId],
      locationId: locationId ? String(locationId) : '',
      allowedPartLocationType: '',
      partActionType: actionType,
      searchType: 'part-and-part-type',
      reportedSerialNumberPartId,
    };
    reportedSerialMutation
      .mutateAsync(data)
      .then((r) => {
        if (!cancelled) setReportedSerialResults(r);
      })
      .catch(() => {
        if (!cancelled) setReportedSerialResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    shouldPrePopulateUniverse,
    reportedSerialNumberPartId,
    actionType,
    locationId,
    clientId,
    contractId,
  ]);

  // Source #2 — installed part types on this job.
  const installedQuery = useGetInteractionsPartRequestFormPartTypes(
    shouldPrePopulateUniverse ? serviceId : 0,
    PartConditionOptions.INSTALLED
  );

  // Source #3 — client/contract part-type universe.
  const contractQuery = useGetPartTypesByClientContract(clientId, contractId);
  const { refetch: refetchContract } = contractQuery;
  useEffect(() => {
    if (shouldPrePopulateUniverse && clientId) refetchContract();
  }, [refetchContract, clientId, contractId, shouldPrePopulateUniverse]);

  // DOA OUT — serialised parts via /partOrPartType + reported-serial hint.
  useEffect(() => {
    if (!isOutSerialised || !isPrePopulateAction || !reportedSerialNumberPartId) {
      setOutSerialResults([]);
      return;
    }
    let cancelled = false;
    const data: PartOrPartTypeSearchData = {
      name: '',
      serialiseType: PartTypeActionLiveSearchItemCategoryCode.Serialised,
      clientContractIds: [clientId, contractId],
      locationId: locationId ? String(locationId) : '',
      allowedPartLocationType: '',
      partActionType: actionType,
      searchType: 'part-and-part-type',
      reportedSerialNumberPartId,
    };
    outSerialMutation
      .mutateAsync(data)
      .then((r) => {
        if (!cancelled) setOutSerialResults(r);
      })
      .catch(() => {
        if (!cancelled) setOutSerialResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    isOutSerialised,
    isPrePopulateAction,
    reportedSerialNumberPartId,
    actionType,
    locationId,
    clientId,
    contractId,
  ]);

  const defaults = useMemo<PartTypeActionLiveSearchItem[]>(() => {
    if (isOutSerialised) return outSerialResults;
    if (shouldPrePopulateUniverse) {
      return buildDefaultPartTypeOptions(
        reportedSerialResults,
        reportedSerialNumberPartId,
        installedQuery.data,
        contractQuery.data
      );
    }
    return [];
  }, [
    isOutSerialised,
    outSerialResults,
    shouldPrePopulateUniverse,
    reportedSerialResults,
    reportedSerialNumberPartId,
    installedQuery.data,
    contractQuery.data,
  ]);

  return { defaults };
};
